import React, { useEffect } from 'react';
import Layout from '../components/Layout';
import Icon3D from '../components/Icon3D';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import {
  ShoppingBag, Stethoscope, ExternalLink, Package, Heart
} from 'lucide-react';
import { trackAffiliateClick } from '../api/endpoints';

// ─── Supplier data ────────────────────────────────────────────────────────────

// Each supplier can have an optional `affiliateUrl` field.
// When present, the card links to the affiliate URL instead of the direct URL.
// Affiliate URLs are populated once the account holder signs up with each program.
// Set affiliateUrl to null (or omit) until a real tracked URL is available.

const SUPPLIERS_BY_COUNTRY = {
  Australia: {
    uniforms: [
      {
        name: 'Mediscrubs',
        description: 'Australian-owned medical scrubs specialist. Quality healthcare workwear with a wide range of colours and styles for students and professionals.',
        url: 'https://www.mediscrubs.com.au',
        affiliateUrl: null, // Contact for direct referral deal
        tag: 'Scrubs & Uniforms',
        colour: 'turquoise',
      },
      {
        name: 'Airmed Scrubs',
        description: 'Premium Australian medical scrubs designed for comfort and durability. Modern styles for healthcare students and professionals.',
        url: 'https://www.airmedscrubs.com.au',
        affiliateUrl: null, // Contact for direct referral deal
        tag: 'Scrubs & Uniforms',
        colour: 'blue',
      },
      {
        name: 'Scrubsy',
        description: 'Affordable, stylish medical scrubs for Australian healthcare students. Student-friendly pricing with nationwide shipping.',
        url: 'https://www.scrubsy.com.au',
        affiliateUrl: null, // Contact for direct referral deal
        tag: 'Scrubs & Uniforms',
        colour: 'indigo',
      },
      {
        name: 'The Scrub Store',
        description: 'Dedicated scrubs retailer with a wide selection of medical uniforms. Quality brands at competitive prices for students and professionals.',
        url: 'https://www.thescrubstore.com.au',
        affiliateUrl: null, // Contact for direct referral deal
        tag: 'Scrubs & Uniforms',
        colour: 'purple',
      },
    ],
    equipment: [
      {
        name: 'Medshop Australia',
        description: 'One-stop shop for medical students: stethoscopes, penlight torches, reflex hammers, otoscopes and clinical accessories. Ships nationwide.',
        url: 'https://www.medshop.com.au',
        affiliateUrl: null, // Has referral program — contact for partner code
        tag: 'Clinical Equipment',
        colour: 'turquoise',
      },
      {
        name: 'TacMed Australia',
        description: 'Tactical and clinical medical supplies for healthcare professionals and students. Quality diagnostic and first-response equipment.',
        url: 'https://tacmedaustralia.com.au',
        affiliateUrl: null,
        tag: 'Medical Supplies',
        colour: 'blue',
      },
      {
        name: 'Team Medical',
        description: 'Australian medical equipment supplier specialising in diagnostic instruments. Stethoscopes, otoscopes and clinical kits for students.',
        url: 'https://www.teammed.com.au',
        affiliateUrl: null,
        tag: 'Diagnostic Equipment',
        colour: 'amber',
      },
      {
        name: 'Amazon AU — Medical Supplies',
        description: 'Wide range of stethoscopes, anatomy models, clinical kits and medical textbooks. Fast shipping with Prime.',
        url: 'https://www.amazon.com.au/s?k=medical+student+supplies',
        affiliateUrl: null, // Replace with: https://www.amazon.com.au/s?k=medical+student+supplies&tag=YOUR-AU-TAG-20
        tag: 'Medical Supplies',
        colour: 'green',
      },
    ],
  },

  'United Kingdom': {
    uniforms: [
      {
        name: 'Uniform Direct UK',
        description: 'NHS-compliant medical uniforms, scrubs and tunics for healthcare students across the UK.',
        url: 'https://www.uniformdirect.com/scrubs',
        affiliateUrl: null,
        tag: 'NHS Scrubs & Uniforms',
        colour: 'blue',
      },
      {
        name: 'Koi Scrubs UK',
        description: 'Fashion-forward medical scrubs for UK healthcare professionals and students. Stylish designs with functional pockets.',
        url: 'https://www.koiscrubs.co.uk/scrubs',
        affiliateUrl: null,
        tag: 'Scrubs',
        colour: 'turquoise',
      },
    ],
    equipment: [
      {
        name: 'Medisave UK',
        description: 'UK\'s leading medical equipment retailer. Stethoscopes, sphygmomanometers and diagnostic kits at student-friendly prices.',
        url: 'https://www.medisave.co.uk',
        affiliateUrl: null, // Has affiliate program — apply directly at medisave.co.uk
        tag: 'Medical Equipment',
        colour: 'green',
      },
      {
        name: 'Amazon UK — Medical Supplies',
        description: 'Stethoscopes, diagnostic kits, anatomy models and clinical accessories. Free delivery with Prime.',
        url: 'https://www.amazon.co.uk/s?k=medical+student+equipment',
        affiliateUrl: null, // Replace with: https://www.amazon.co.uk/s?k=medical+student+equipment&tag=YOUR-UK-TAG-21
        tag: 'Medical Supplies',
        colour: 'amber',
      },
    ],
  },

  'United States': {
    uniforms: [
      {
        name: 'FIGS',
        description: 'Premium healthcare apparel designed for modern medical students and clinicians. Technical fabrics and stylish cuts.',
        url: 'https://www.wearfigs.com/shop/scrubs',
        affiliateUrl: null, // Apply at wearfigs.com ambassador program — 10-15% commission
        tag: 'Premium Scrubs',
        colour: 'turquoise',
      },
      {
        name: 'Scrubs & Beyond',
        description: 'America\'s largest medical uniform retailer with thousands of styles for students and healthcare professionals.',
        url: 'https://www.scrubsandbeyond.com/shop',
        affiliateUrl: null, // Join via ShareASale — 5-8% commission
        tag: 'Scrubs & Uniforms',
        colour: 'blue',
      },
    ],
    equipment: [
      {
        name: 'Bound Tree Medical',
        description: 'Comprehensive medical supplies and equipment. Competitive pricing for students with a full diagnostic range.',
        url: 'https://www.boundtree.com',
        affiliateUrl: null,
        tag: 'Medical Equipment',
        colour: 'green',
      },
      {
        name: 'Amazon US — Littmann Stethoscopes',
        description: 'The world\'s most trusted stethoscope brand. Wide range of models for medical students and professionals.',
        url: 'https://www.amazon.com/s?k=3M+Littmann+Stethoscope',
        affiliateUrl: null, // Replace with: https://www.amazon.com/s?k=3M+Littmann+Stethoscope&tag=YOUR-US-TAG-20
        tag: 'Stethoscopes',
        colour: 'amber',
      },
    ],
  },
};

// Generic fallback for unrecognised countries
const GENERIC_SUPPLIERS = {
  uniforms: [
    {
      name: 'FIGS (Ships Worldwide)',
      description: 'Premium scrubs that ship internationally. Trusted by medical students and professionals worldwide.',
      url: 'https://www.wearfigs.com/shop/scrubs',
      affiliateUrl: null, // Apply at wearfigs.com ambassador program
      tag: 'Premium Scrubs',
      colour: 'turquoise',
    },
    {
      name: 'Infectious Clothing Co.',
      description: 'Australian medical scrubs brand that ships internationally. Student-friendly pricing and quality workwear.',
      url: 'https://www.infectiousclothing.com.au/shop',
      affiliateUrl: null,
      tag: 'Scrubs & Uniforms',
      colour: 'blue',
    },
  ],
  equipment: [
    {
      name: 'Medshop Australia (Ships Worldwide)',
      description: 'Clinical equipment and stethoscopes with international shipping. Student-friendly pricing.',
      url: 'https://www.medshop.com.au',
      affiliateUrl: null,
      tag: 'Clinical Equipment',
      colour: 'green',
    },
    {
      name: '3M Littmann Stethoscopes',
      description: "The world's most trusted stethoscope brand. Available globally through authorised retailers.",
      url: 'https://www.littmann.com/3M/en_US/littmann-stethoscopes/',
      affiliateUrl: null,
      tag: 'Stethoscopes',
      colour: 'amber',
    },
  ],
};

// ─── Supplier card ────────────────────────────────────────────────────────────

const SupplierCard = ({ supplier, category }) => {
  // Use affiliate URL when available, otherwise fall back to direct URL
  const linkUrl = supplier.affiliateUrl || supplier.url;

  const handleClick = () => {
    // Fire-and-forget — don't block navigation
    trackAffiliateClick({
      supplier_name: supplier.name,
      supplier_url: linkUrl,
      category: category || '',
    }).catch(() => {}); // silently ignore if not logged in or fails
  };

  return (
    <div
      className="bg-white rounded-2xl p-6 flex flex-col"
      style={{ boxShadow: 'var(--shadow-card)' }}
    >
      <div className="flex items-start gap-4 mb-4">
        <Icon3D icon={ShoppingBag} colour={supplier.colour} size="md" />
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-slate-900 text-lg leading-tight">{supplier.name}</h3>
          <span className="inline-block mt-1 text-xs font-semibold px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
            {supplier.tag}
          </span>
        </div>
      </div>
      <p className="text-sm text-slate-500 flex-1 mb-5 leading-relaxed">{supplier.description}</p>
      <a
        href={linkUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        className="w-full py-2.5 rounded-xl text-white font-semibold text-sm text-center relative overflow-hidden"
        style={{ background: 'var(--gradient-primary)', boxShadow: 'var(--shadow-3d-btn)' }}
      >
        <div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full"
          style={{ animation: 'shimmer 2.5s infinite' }}
        />
        <span className="relative z-10 flex items-center justify-center gap-2">
          Shop Now <ExternalLink className="w-4 h-4" />
        </span>
      </a>
    </div>
  );
};

// ─── Section header ───────────────────────────────────────────────────────────

const SectionHeader = ({ icon, colour, title, subtitle }) => (
  <div className="flex items-center gap-4 mb-6">
    <Icon3D icon={icon} colour={colour} size="md" />
    <div>
      <h2 className="text-xl font-bold text-slate-900">{title}</h2>
      <p className="text-sm text-slate-500">{subtitle}</p>
    </div>
  </div>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

const UniformsPage = () => {
  const { tenant } = useTenant();
  const { user } = useAuth();
  const tenantName = tenant?.name || 'MedMCQ';

  useEffect(() => {
    document.title = `Uniforms & Equipment | ${tenantName}`;
  }, [tenantName]);

  // Determine country — fall back to Australia
  const userCountry = user?.country || 'Australia';
  const countryData = SUPPLIERS_BY_COUNTRY[userCountry] || GENERIC_SUPPLIERS;
  const displayCountry = SUPPLIERS_BY_COUNTRY[userCountry] ? userCountry : 'International';

  return (
    <Layout>
      {/* ── Header banner ── */}
      <div
        className="text-white relative overflow-hidden"
        style={{
          background: 'var(--gradient-primary)',
          boxShadow: '0 8px 32px rgba(6,182,212,0.35)',
        }}
      >
        {/* Hero banner — no shimmer on static elements */}
        <div className="max-w-7xl mx-auto px-4 py-12 relative z-10">
          <div className="flex items-center gap-4 mb-4">
            <Icon3D icon={ShoppingBag} colour="turquoise" size="lg" />
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">Uniforms &amp; Equipment</h1>
              <p className="text-cyan-100 mt-1">
                Curated suppliers for {displayCountry} medical students
              </p>
            </div>
          </div>
          <p className="text-cyan-50 max-w-2xl text-sm leading-relaxed">
            Everything you need as a medical student — from clinical scrubs to diagnostic equipment.
            We've partnered with trusted suppliers to bring you the best options for your studies.
          </p>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="max-w-7xl mx-auto px-4 py-10">

        {/* Scrubs & Uniforms section */}
        <div className="mb-12">
          <SectionHeader
            icon={Heart}
            colour="turquoise"
            title="Medical Uniforms &amp; Scrubs"
            subtitle={`Top-rated scrubs and uniform suppliers for ${displayCountry} students`}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {countryData.uniforms.map((s) => (
              <SupplierCard key={s.name} supplier={s} category="uniforms" />
            ))}
          </div>
        </div>

        {/* Equipment section */}
        <div className="mb-12">
          <SectionHeader
            icon={Stethoscope}
            colour="blue"
            title="Clinical Equipment &amp; Accessories"
            subtitle="Stethoscopes, diagnostic kits and student supplies"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {countryData.equipment.map((s) => (
              <SupplierCard key={s.name} supplier={s} category="equipment" />
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-slate-50 rounded-2xl p-6 flex items-start gap-3 border border-slate-200" style={{ boxShadow: 'var(--shadow-card)' }}>
          <Package className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-slate-500 leading-relaxed">
            <strong className="text-slate-700">Affiliate disclosure:</strong> Some links on this page are affiliate links.
            If you purchase through these links, {tenantName} may earn a small commission at no extra cost to you.
            This helps us keep the platform free for students. We only recommend suppliers we believe offer genuine value —
            affiliate status never influences our recommendations.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default UniformsPage;
